import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useSubscription } from '@/queries/subscriptions';
import { useAuth } from '@/contexts/AuthContext';
import { CircularLoader } from '@/components/CircularLoader';
import { api } from '@/lib/api';
import { apiConfig } from '@/config/api';
import styles from '../account.module.scss';
import SproutIcon from '@/assets/common/sprout.svg?react';
import WavesIcon from '@/assets/common/waves-arrow-down.svg?react';
import EarthIcon from '@/assets/common/earth.svg?react';

export const Route = createFileRoute('/account/')({
  component: AccountIndexPage,
});

function AccountIndexPage() {
  // In production, the settings page is not available during Alpha
  if (apiConfig.getEnvironment() === 'production') {
    return <Navigate to="/account/usage" />;
  }

  const { user } = useAuth();
  const { data: subscription, isLoading } = useSubscription();

  const handleManageSubscription = async () => {
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      alert('Failed to open customer portal. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.account__loading}>
        <CircularLoader />
        <p>Loading account details...</p>
      </div>
    );
  }

  const currentTier = subscription?.tier || 'foundation';
  const status = subscription?.status || 'active';
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : null;

  const tierNames: Record<string, string> = {
    foundation: 'Foundation',
    deep_dive: 'Deep Dive',
    mastermind: 'Mastermind',
  };

  const tierPrices: Record<string, string> = {
    foundation: 'Free',
    deep_dive: '$4.99/month',
    mastermind: '$7.99/month',
  };

  return (
    <motion.div
      className={styles.account__container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h1 className={styles.account__title}>Account Settings</h1>

      {/* Current Subscription Card */}
      <motion.div
        className={styles.account__card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h2 className={styles.account__cardTitle}>Current Subscription</h2>

        <div className={styles.account__subscription}>
          <div className={styles.account__tierBadge}>
            <span className={styles.account__tierIcon}>
              {currentTier === 'foundation' && <SproutIcon width={24} height={24} />}
              {currentTier === 'deep_dive' && <WavesIcon width={24} height={24} />}
              {currentTier === 'mastermind' && <EarthIcon width={24} height={24} />}
            </span>
            <div>
              <div className={styles.account__tierName}>
                {tierNames[currentTier]}
              </div>
              <div className={styles.account__tierPrice}>
                {tierPrices[currentTier]}
              </div>
            </div>
          </div>

          <div className={styles.account__subscriptionDetails}>
            <div className={styles.account__detail}>
              <span className={styles.account__detailLabel}>Status:</span>
              <span
                className={`${styles.account__detailValue} ${status === 'active'
                  ? styles['account__detailValue--active']
                  : styles['account__detailValue--inactive']
                  }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </div>

            {periodEnd && currentTier !== 'foundation' && (
              <div className={styles.account__detail}>
                <span className={styles.account__detailLabel}>
                  {subscription?.cancel_at_period_end
                    ? 'Active until:'
                    : 'Next billing:'}
                </span>
                <span className={styles.account__detailValue}>
                  {periodEnd.toLocaleDateString()}
                </span>
              </div>
            )}

            {subscription?.cancel_at_period_end && (
              <div className={styles.account__warning}>
                Your subscription will be canceled at the end of the current
                billing period.
              </div>
            )}
          </div>
        </div>

        <div className={styles.account__actions}>
          {currentTier !== 'foundation' && (
            <motion.button
              className={styles.account__button}
              onClick={handleManageSubscription}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Manage Subscription
            </motion.button>
          )}

          <Link to="/pricing">
            <motion.button
              className={`${styles.account__button} ${styles['account__button--secondary']}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {currentTier === 'foundation' ? 'Upgrade Plan' : 'Change Plan'}
            </motion.button>
          </Link>
        </div>
      </motion.div>

      {/* Usage Statistics Link */}
      <motion.div
        className={styles.account__card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className={styles.account__cardTitle}>Usage & Analytics</h2>
        <p className={styles.account__cardDescription}>
          View your quiz generation usage, saved quizzes, and activity
          history.
        </p>
        <Link to="/account/usage">
          <motion.button
            className={styles.account__button}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            View Usage Statistics →
          </motion.button>
        </Link>
      </motion.div>

      {/* Account Info */}
      <motion.div
        className={styles.account__card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className={styles.account__cardTitle}>Account Information</h2>
        <div className={styles.account__info}>
          <div className={styles.account__detail}>
            <span className={styles.account__detailLabel}>Email:</span>
            <span className={styles.account__detailValue}>{user?.email}</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
